import { useEffect, useState } from "react";
import api from "../lib/api";
import { fmtTRY } from "../lib/format";
import { validateUpload } from "../lib/upload";
import { PageHeader, StatusBadge } from "../components/Common";
import { useAuth } from "../context/AuthContext";
import { Trash, PencilSimple, X, Car, Wrench, FolderOpen, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUSES = [
  { v: "available", l: "Müsait" },
  { v: "rented", l: "Kirada" },
  { v: "maintenance", l: "Bakımda" },
];

export default function Fleet() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [maintVeh, setMaintVeh] = useState(null);
  const [docVeh, setDocVeh] = useState(null);

  const load = () => api.get("/vehicles").then(({ data }) => setItems(data.filter((v) => v.status !== "sold")));
  useEffect(() => { load(); }, []);

  const openEdit = (v) => { setEditing(v); setForm({ ...v, next_maintenance_date: v.next_maintenance_date ? String(v.next_maintenance_date).slice(0, 10) : "" }); setModal(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, year: parseInt(form.year), daily_rate: parseFloat(form.daily_rate) || 0, next_maintenance_date: form.next_maintenance_date || null };
    try {
      if (editing) await api.put(`/vehicles/${editing.id}`, payload);
      toast.success("Araç güncellendi");
      setModal(false); load();
    } catch { toast.error("İşlem başarısız"); }
  };
  const remove = async (id) => { if (!window.confirm("Bu aracı silmek istediğinize emin misiniz?")) return; await api.delete(`/vehicles/${id}`); toast.success("Araç silindi"); load(); };

  const counts = {
    total: items.length,
    rented: items.filter(v => v.status === "rented").length,
    available: items.filter(v => v.status === "available").length,
    maintenance: items.filter(v => v.status === "maintenance").length,
  };

  return (
    <div className="p-8 max-w-[1400px]">
      <PageHeader title="Filo Durumu" subtitle="Araç kullanım durumu (Müsait / Kirada / Bakımda) ve bakım takibi. Araç alım/satımı için Envanter sayfasını kullanın." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 animate-fade-up">
        <Mini label="Toplam Araç" value={counts.total} />
        <Mini label="Kirada" value={counts.rented} cls="text-blue-600" />
        <Mini label="Müsait" value={counts.available} cls="text-green-600" />
        <Mini label="Bakımda" value={counts.maintenance} cls="text-red-600" />
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden animate-fade-up" data-testid="vehicles-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50">
              <th className="text-left font-semibold text-zinc-600 px-4 py-3 text-xs uppercase tracking-wider">Plaka</th>
              <th className="text-left font-semibold text-zinc-600 px-4 py-3 text-xs uppercase tracking-wider">Araç</th>
              <th className="text-left font-semibold text-zinc-600 px-4 py-3 text-xs uppercase tracking-wider">Yıl</th>
              <th className="text-center font-semibold text-zinc-600 px-4 py-3 text-xs uppercase tracking-wider">Durum</th>
              <th className="text-right font-semibold text-zinc-600 px-4 py-3 text-xs uppercase tracking-wider">Aylık Ücret</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((v) => (
              <tr key={v.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`vehicle-row-${v.id}`}>
                <td className="px-4 py-3 font-mono font-semibold text-zinc-900">{v.plate}</td>
                <td className="px-4 py-3 text-zinc-700"><span className="inline-flex items-center gap-2"><Car size={16} className="text-zinc-400" />{v.brand} {v.model}</span></td>
                <td className="px-4 py-3 font-mono text-zinc-500">{v.year}</td>
                <td className="px-4 py-3 text-center"><StatusBadge status={v.status} /></td>
                <td className="px-4 py-3 text-right font-mono text-zinc-900 tabular-nums">{fmtTRY(v.daily_rate)}</td>
                <td className="px-4 py-3"><MaintenanceCell date={v.next_maintenance_date} /></td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => setMaintVeh(v)} data-testid={`maintenance-vehicle-${v.id}`} title="Bakım Geçmişi" className="p-1.5 text-zinc-400 hover:text-[#002FA7] transition-colors"><Wrench size={16} /></button>
                    <button onClick={() => setDocVeh(v)} data-testid={`documents-vehicle-${v.id}`} title="Belgeler" className="p-1.5 text-zinc-400 hover:text-[#002FA7] transition-colors"><FolderOpen size={16} /></button>
                    <button onClick={() => openEdit(v)} data-testid={`edit-vehicle-${v.id}`} className="p-1.5 text-zinc-400 hover:text-[#002FA7] transition-colors"><PencilSimple size={16} /></button>
                    {user?.role === "admin" && <button onClick={() => remove(v.id)} data-testid={`delete-vehicle-${v.id}`} className="p-1.5 text-zinc-400 hover:text-red-600 transition-colors"><Trash size={16} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setModal(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white rounded-sm border border-zinc-200 w-full max-w-md p-6 animate-fade-up" data-testid="vehicle-modal">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-xl font-semibold">{editing ? "Aracı Düzenle" : "Yeni Araç"}</h3>
              <button type="button" onClick={() => setModal(false)}><X size={20} className="text-zinc-400 hover:text-zinc-900" /></button>
            </div>
            <div className="space-y-4">
              <F label="Plaka"><input required value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value })} data-testid="veh-plate" className="inp font-mono" placeholder="34 ABC 123" /></F>
              <div className="grid grid-cols-2 gap-3">
                <F label="Marka"><input required value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="veh-brand" className="inp" placeholder="Fiat" /></F>
                <F label="Model"><input required value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="veh-model" className="inp" placeholder="Egea" /></F>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <F label="Yıl"><input type="number" required value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="veh-year" className="inp font-mono" /></F>
                <F label="Aylık Ücret (₺)"><input type="number" value={form.daily_rate} onChange={(e) => setForm({ ...form, daily_rate: e.target.value })} data-testid="veh-rate" className="inp font-mono" /></F>
              </div>
              <F label="Durum">
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="veh-status" className="inp">
                  {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                </select>
              </F>
              <F label="Sonraki Bakım Tarihi"><input type="date" value={form.next_maintenance_date || ""} onChange={(e) => setForm({ ...form, next_maintenance_date: e.target.value })} data-testid="veh-maintenance-date" className="inp font-mono" /></F>
            </div>
            <button type="submit" data-testid="veh-submit" className="w-full mt-6 bg-zinc-950 text-white rounded-sm py-2.5 text-sm font-semibold hover:bg-zinc-800 transition-colors">Kaydet</button>
          </form>
        </div>
      )}
      {maintVeh && <MaintenanceModal vehicle={maintVeh} onClose={() => setMaintVeh(null)} canDelete={user?.role === "admin"} />}
      {docVeh && <DocumentsModal vehicle={docVeh} onClose={() => setDocVeh(null)} canDelete={user?.role === "admin"} />}
    </div>
  );
}

function Mini({ label, value, cls }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-5">
      <span className="text-xs uppercase tracking-[0.15em] font-semibold text-zinc-500">{label}</span>
      <div className={`font-mono text-3xl font-bold mt-2 tabular-nums ${cls || "text-zinc-950"}`}>{value}</div>
    </div>
  );
}
function F({ label, children }) {
  return (<div><label className="block text-xs uppercase tracking-[0.15em] font-semibold text-zinc-500 mb-1.5">{label}</label>{children}</div>);
}

function MaintenanceCell({ date }) {
  if (!date) return <span className="text-zinc-300 text-xs">—</span>;
  const d = new Date(String(date).slice(0, 10));
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((d - today) / 86400000);
  const label = d.toLocaleDateString("tr-TR");
  let cls = "text-zinc-600";
  let note = "";
  if (days < 0) { cls = "text-red-600 font-semibold"; note = `${Math.abs(days)} gün gecikti`; }
  else if (days <= 5) { cls = "text-red-600 font-semibold"; note = `${days} gün kaldı`; }
  else if (days <= 15) { cls = "text-yellow-600 font-semibold"; note = `${days} gün kaldı`; }
  return (
    <div className="flex flex-col">
      <span className={`font-mono text-sm ${cls}`}>{label}</span>
      {note && <span className={`text-[11px] ${cls}`}>{note}</span>}
    </div>
  );
}

const DOC_TYPES = ["Ruhsat", "Kasko", "Sigorta", "Muayene", "Diğer"];

function fmtDate(s) {
  if (!s) return "—";
  return new Date(String(s).slice(0, 10)).toLocaleDateString("tr-TR");
}

function expiryNote(s) {
  if (!s) return null;
  const d = new Date(String(s).slice(0, 10));
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((d - today) / 86400000);
  if (days < 0) return { text: `${Math.abs(days)} gün önce doldu`, cls: "text-red-600" };
  if (days <= 7) return { text: `${days} gün kaldı`, cls: "text-red-600" };
  if (days <= 30) return { text: `${days} gün kaldı`, cls: "text-yellow-600" };
  return null;
}

function MaintenanceModal({ vehicle, onClose, canDelete }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), description: "", cost: "", workshop: "" });
  const [saving, setSaving] = useState(false);
  const load = () => api.get(`/vehicles/${vehicle.id}/maintenance`).then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/vehicles/${vehicle.id}/maintenance`, { ...form, cost: parseFloat(form.cost) || 0 });
      toast.success("Bakım kaydı eklendi");
      setForm({ date: new Date().toISOString().slice(0, 10), description: "", cost: "", workshop: "" });
      load();
    } catch { toast.error("Kayıt eklenemedi"); } finally { setSaving(false); }
  };
  const del = async (id) => { if (!window.confirm("Bu bakım kaydını silmek istiyor musunuz?")) return; await api.delete(`/vehicles/${vehicle.id}/maintenance/${id}`); toast.success("Kayıt silindi"); load(); };
  const totalCost = items.reduce((s, i) => s + (i.cost || 0), 0);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-sm border border-zinc-200 w-full max-w-2xl p-6 animate-fade-up max-h-[90vh] overflow-y-auto" data-testid="maintenance-modal">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display text-xl font-semibold flex items-center gap-2"><Wrench size={20} className="text-[#002FA7]" /> Bakım Geçmişi</h3>
          <button onClick={onClose}><X size={20} className="text-zinc-400 hover:text-zinc-900" /></button>
        </div>
        <p className="text-sm text-zinc-500 mb-5 font-mono">{vehicle.plate} · {vehicle.brand} {vehicle.model}</p>

        <form onSubmit={add} className="grid grid-cols-2 gap-3 bg-zinc-50 border border-zinc-200 rounded-sm p-4 mb-5">
          <F label="Tarih"><input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="maint-date" className="inp font-mono" /></F>
          <F label="Maliyet (₺)"><input type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} data-testid="maint-cost" className="inp font-mono" placeholder="0" /></F>
          <F label="İşlem / Açıklama"><input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="maint-desc" className="inp" placeholder="Yağ değişimi, fren balatası..." /></F>
          <F label="Servis / Atölye"><input value={form.workshop} onChange={(e) => setForm({ ...form, workshop: e.target.value })} data-testid="maint-workshop" className="inp" placeholder="Yetkili servis" /></F>
          <div className="col-span-2"><button type="submit" disabled={saving} data-testid="maint-submit" className="w-full bg-zinc-950 text-white rounded-sm py-2.5 text-sm font-semibold hover:bg-zinc-800 transition-colors disabled:opacity-50">{saving ? "Kaydediliyor..." : "Bakım Kaydı Ekle"}</button></div>
        </form>

        <div className="flex items-center justify-between mb-2">
          <span className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Kayıtlar ({items.length})</span>
          <span className="text-sm font-mono font-semibold text-zinc-900">Toplam: {fmtTRY(totalCost)}</span>
        </div>
        {items.length === 0 ? (
          <div className="text-sm text-zinc-400 py-6 text-center border border-dashed border-zinc-200 rounded-sm">Henüz bakım kaydı yok</div>
        ) : (
          <div className="space-y-2" data-testid="maintenance-list">
            {items.map((m) => (
              <div key={m.id} className="flex items-center justify-between border border-zinc-200 rounded-sm px-4 py-3" data-testid={`maint-row-${m.id}`}>
                <div>
                  <div className="text-sm font-medium text-zinc-900">{m.description}</div>
                  <div className="text-xs text-zinc-500 mt-0.5 font-mono">{fmtDate(m.date)}{m.workshop ? ` · ${m.workshop}` : ""}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm font-semibold text-zinc-900 tabular-nums">{fmtTRY(m.cost)}</span>
                  {canDelete && <button onClick={() => del(m.id)} data-testid={`maint-delete-${m.id}`} className="p-1 text-zinc-300 hover:text-red-600"><Trash size={15} /></button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DocumentsModal({ vehicle, onClose, canDelete }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ doc_type: "Ruhsat", name: "", expiry_date: "", file_path: null });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const load = () => api.get(`/vehicles/${vehicle.id}/documents`).then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!validateUpload(file, "document")) { e.target.value = ""; return; }
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, file_path: data.path, name: f.name || file.name }));
      toast.success("Dosya yüklendi");
    } catch { toast.error("Yükleme başarısız"); } finally { setUploading(false); }
  };
  const add = async (e) => {
    e.preventDefault();
    if (!form.file_path) { toast.error("Lütfen bir dosya yükleyin"); return; }
    setSaving(true);
    try {
      await api.post(`/vehicles/${vehicle.id}/documents`, { ...form, expiry_date: form.expiry_date || null });
      toast.success("Belge kaydedildi");
      setForm({ doc_type: "Ruhsat", name: "", expiry_date: "", file_path: null });
      load();
    } catch { toast.error("Belge kaydedilemedi"); } finally { setSaving(false); }
  };
  const del = async (id) => { if (!window.confirm("Bu belgeyi silmek istiyor musunuz?")) return; await api.delete(`/vehicles/${vehicle.id}/documents/${id}`); toast.success("Belge silindi"); load(); };
  const view = async (path) => {
    try { const { data } = await api.get(`/files/${path}`, { responseType: "blob" }); window.open(URL.createObjectURL(data), "_blank"); }
    catch { toast.error("Belge açılamadı"); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-sm border border-zinc-200 w-full max-w-2xl p-6 animate-fade-up max-h-[90vh] overflow-y-auto" data-testid="documents-modal">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display text-xl font-semibold flex items-center gap-2"><FolderOpen size={20} className="text-[#002FA7]" /> Belge Kütüphanesi</h3>
          <button onClick={onClose}><X size={20} className="text-zinc-400 hover:text-zinc-900" /></button>
        </div>
        <p className="text-sm text-zinc-500 mb-5 font-mono">{vehicle.plate} · {vehicle.brand} {vehicle.model}</p>

        <form onSubmit={add} className="grid grid-cols-2 gap-3 bg-zinc-50 border border-zinc-200 rounded-sm p-4 mb-5">
          <F label="Belge Türü">
            <select value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })} data-testid="doc-type" className="inp">
              {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </F>
          <F label="Son Geçerlilik Tarihi"><input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="doc-expiry" className="inp font-mono" /></F>
          <F label="Belge Adı (opsiyonel)"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="doc-name" className="inp" placeholder="Kasko poliçesi 2026" /></F>
          <F label="Dosya">
            <label className={`flex items-center gap-2 border rounded-sm px-3 py-2 text-sm cursor-pointer hover:bg-white ${form.file_path ? "border-green-400 text-green-700" : "border-zinc-300 text-zinc-600"}`} data-testid="doc-file-label">
              {uploading ? "Yükleniyor..." : form.file_path ? "Yüklendi ✓ (değiştir)" : "Görsel / PDF yükle"}
              <input type="file" accept="image/*,.pdf" onChange={upload} data-testid="doc-file" className="hidden" />
            </label>
          </F>
          <div className="col-span-2"><button type="submit" disabled={saving || uploading} data-testid="doc-submit" className="w-full bg-zinc-950 text-white rounded-sm py-2.5 text-sm font-semibold hover:bg-zinc-800 transition-colors disabled:opacity-50">{saving ? "Kaydediliyor..." : "Belge Ekle"}</button></div>
        </form>

        <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500 mb-2">Belgeler ({items.length})</div>
        {items.length === 0 ? (
          <div className="text-sm text-zinc-400 py-6 text-center border border-dashed border-zinc-200 rounded-sm">Henüz belge yok</div>
        ) : (
          <div className="space-y-2" data-testid="documents-list">
            {items.map((d) => {
              const note = expiryNote(d.expiry_date);
              return (
                <div key={d.id} className="flex items-center justify-between border border-zinc-200 rounded-sm px-4 py-3" data-testid={`doc-row-${d.id}`}>
                  <div>
                    <div className="text-sm font-medium text-zinc-900"><span className="inline-block text-[10px] uppercase tracking-wider font-semibold text-white bg-[#002FA7] rounded-sm px-1.5 py-0.5 mr-2">{d.doc_type}</span>{d.name || "Belge"}</div>
                    <div className="text-xs text-zinc-500 mt-1 font-mono">Geçerlilik: {fmtDate(d.expiry_date)} {note && <span className={`font-semibold ${note.cls}`}>· {note.text}</span>}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => view(d.file_path)} data-testid={`doc-view-${d.id}`} className="p-1.5 text-zinc-400 hover:text-[#002FA7]" title="Görüntüle"><ArrowSquareOut size={16} /></button>
                    {canDelete && <button onClick={() => del(d.id)} data-testid={`doc-delete-${d.id}`} className="p-1 text-zinc-300 hover:text-red-600"><Trash size={15} /></button>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
